import { z } from 'zod'
import { CACHE_MODE_VALUES, TILE_SOURCE_KEYS } from '../config/tileSources'
import { BACKGROUND_MAP_IDS } from '../map/backgroundMapIds'

// Single source of truth for the `/` route's URL search params (TanStack Router
// `validateSearch`), replacing the old per-param nuqs parsers.
//
// Wire-format note: the delimited string params (`map`, `bbox`, `layers`) are kept as
// raw strings here and parsed into rich types inside their wrapper hooks. This keeps the
// URL byte-compatible with the previous nuqs encoding (esp. `map=z/lat/lng`, which is
// load-bearing for existing shared links). `tags` and `highlight` are JSON values, which Router's default
// search (de)serializer round-trips exactly as nuqs `parseAsJson` did.

// A filter matches features whose `key` equals any of `values` (OR); filters combine with AND.
export const tagFiltersSchema = z.array(
  z.object({ key: z.string(), values: z.array(z.string()).min(1) }),
)
export type TagFilterItem = z.infer<typeof tagFiltersSchema>[number]

export const propertyHighlightsSchema = z.array(
  z.object({ key: z.string(), value: z.string().optional() }),
)
export type PropertyHighlightItem = z.infer<typeof propertyHighlightsSchema>[number]

// `category` is highlighted until the user changes the list; an emptied list stays in the URL as `highlight=[]`.
const DEFAULT_HIGHLIGHTS: PropertyHighlightItem[] = [{ key: 'category' }]

export const appSearchSchema = z.object({
  // `z/lat/lng` — parsed by useMapParam. Absent → map fallback.
  map: z.string().optional().catch(undefined),
  source: z.enum(TILE_SOURCE_KEYS).default('Production').catch('Production'),
  cache: z.enum(CACHE_MODE_VALUES).default('cached').catch('cached'),
  background: z.enum(BACKGROUND_MAP_IDS).default('none').catch('none'),
  // Comma-joined layer ids ("a,b,c") — split/joined by useActiveLayers.
  layers: z.string().default('').catch(''),
  search: z.string().default('').catch(''),
  tags: tagFiltersSchema.default([]).catch([]),
  highlight: propertyHighlightsSchema.default(DEFAULT_HIGHLIGHTS).catch(DEFAULT_HIGHLIGHTS),
  panel: z.boolean().default(true).catch(true),
  // Comma-joined overlay ids ("offset,numbers") — split/joined by useMapOverlays.
  overlays: z.string().default('offset').catch('offset'),
  // `minLng,minLat,maxLng,maxLat` — parsed by useBboxParam. Absent → bbox mode off.
  bbox: z.string().optional().catch(undefined),
})

export type AppSearch = z.infer<typeof appSearchSchema>

// Values stripped from the URL when a param equals its default (clean URLs, like nuqs).
// `map`/`bbox` are optional (omitted when undefined) so they need no entry here.
export const appSearchDefaults = {
  source: 'Production',
  cache: 'cached',
  background: 'none',
  layers: '',
  search: '',
  tags: [],
  highlight: DEFAULT_HIGHLIGHTS,
  panel: true,
  overlays: 'offset',
} satisfies Partial<AppSearch>
